/**
 * 账号作业明细：圈选区间内各作业账号的分环节作业量。移植产品原型 page.tsx 的「账号作业明细」tab。
 *
 * 账号角色 / 账号名是后端数据，原样展示；只有前端兜底的「其他账号」「系统 / 未知」走 i18n。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ACCT_KINDS, C } from '../constants';
import type { OpsAcctRow } from '../types';
import { useFmt } from '../lib/useFmt';
import { I18N } from '../lib/useLabels';

const GRID = '150px 1fr repeat(5, 88px) 96px';
/** 按合计取前多少个账号 */
const TOP_N = 30;

interface Props {
    rows: OpsAcctRow[];
    hub: string;
    customer: string;
    /** 圈选区间内的小时（hub 当地时间 'YYYY-MM-DD HH'） */
    labels: string[];
}

const AccountTable: React.FC<Props> = ({ rows, hub, customer, labels }) => {
    const { t } = useTranslation();
    const fmt = useFmt();

    const acctRows = React.useMemo(() => {
        const inRange = new Set(labels);
        const m = new Map<string, { role: string | null; name: string | null; isOther: boolean; v: Record<string, number> }>();
        for (const r of rows) {
            if (r.hub !== hub || !inRange.has(r.hourKey) || (customer && r.customer !== customer)) continue;
            const key = r.isOther ? '\u0000other' : `${r.role ?? '\u0000'}|${r.name ?? '\u0000'}`;
            if (!m.has(key)) m.set(key, { role: r.role, name: r.name, isOther: r.isOther, v: {} });
            const a = m.get(key)!;
            a.v[r.kind] = (a.v[r.kind] ?? 0) + r.count;
        }
        return [...m.entries()]
            .map(([key, a]) => ({ key, ...a, total: Object.values(a.v).reduce((x, y) => x + y, 0) }))
            .sort((a, b) => b.total - a.total)
            .slice(0, TOP_N);
    }, [rows, hub, customer, labels]);

    return (
        <div className="mt-3">
            <div className="mb-1.5 text-[14px] font-semibold" style={{ color: C.text1 }}>
                {t(`${I18N}.acct.title`, { n: labels.length })}
                <span className="ml-2 text-[12.5px] font-normal" style={{ color: C.text3 }}>
                    {t(`${I18N}.acct.hint`, { n: TOP_N })}
                </span>
            </div>
            <div className="overflow-x-auto">
                <div style={{ minWidth: 760 }}>
                    <div
                        className="grid text-[12.5px]"
                        style={{
                            gridTemplateColumns: GRID,
                            color: C.text2,
                            background: C.headBg,
                            borderBottom: `1px solid ${C.border}`,
                            padding: '7px 6px'
                        }}
                    >
                        <div>{t(`${I18N}.acct.colRole`)}</div>
                        <div>{t(`${I18N}.acct.colName`)}</div>
                        {ACCT_KINDS.map((k) => (
                            <div key={k.k} className="text-right">
                                {t(`${I18N}.opsKinds.${k.k}`)}
                            </div>
                        ))}
                        <div className="text-right">{t(`${I18N}.acct.colTotal`)}</div>
                    </div>
                    {acctRows.length === 0 ? (
                        <div className="py-4 text-center text-[13px]" style={{ color: C.text3 }}>
                            {t(`${I18N}.acct.empty`)}
                        </div>
                    ) : (
                        acctRows.map((a) => (
                            <div
                                key={a.key}
                                className="grid text-[13px]"
                                style={{
                                    gridTemplateColumns: GRID,
                                    color: C.text1,
                                    borderBottom: `1px solid ${C.borderLight}`,
                                    padding: '6px 6px'
                                }}
                            >
                                <div style={{ color: C.text2 }}>
                                    {a.isOther ? '-' : a.role ?? t(`${I18N}.acct.systemRole`)}
                                </div>
                                <div className="truncate">{a.isOther ? t(`${I18N}.acct.other`) : a.name ?? '-'}</div>
                                {ACCT_KINDS.map((k) => (
                                    <div key={k.k} className="text-right tabular-nums">
                                        {a.v[k.k] ? fmt.num(a.v[k.k]) : <span style={{ color: C.text3 }}>-</span>}
                                    </div>
                                ))}
                                <div className="text-right font-semibold tabular-nums">{fmt.num(a.total)}</div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default AccountTable;
